// backoffice/src/pages/mockupExport.ts
import { toPng } from 'html-to-image';
import { FFmpeg } from '@ffmpeg/ffmpeg';
// El worker de la clase FFmpeg se crea con `new Worker(new URL('./worker.js',
// import.meta.url), { type: 'module' })`. Bajo el optimizador de deps de Vite
// esa URL relativa no resuelve y load() se cuelga (dev) o rechaza sin mensaje
// (prod → "Error: undefined"). Con `?worker&url` Vite empaqueta el worker y sus
// imports en un fichero autocontenido y nos da una URL estable para pasarla
// como classWorkerURL en dev y en build.
import ffmpegClassWorkerURL from '@ffmpeg/ffmpeg/worker?worker&url';
import { toBlobURL } from '@ffmpeg/util';
import { coverSourceRect, frameTimes } from './mockupGeometry';

export type MediaEl = HTMLImageElement | HTMLVideoElement;
type Rect = { x: number; y: number; w: number; h: number };

/** "#rrggbb" -> "0xrrggbb" para el filtro color de ffmpeg; deja otros valores igual. */
function toFFmpegColor(hex: string): string {
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return `0x${hex.slice(1)}`;

  return hex;
}

/** Dimensiones naturales del media (video: videoWidth/Height; img: naturalW/H). */
function mediaNaturalSize(media: MediaEl): { w: number; h: number } {
  if (media instanceof HTMLVideoElement) {
    return { w: media.videoWidth, h: media.videoHeight };
  }

  return { w: media.naturalWidth, h: media.naturalHeight };
}

const DPR = 2; // paridad con el deviceScaleFactor:2 del export anterior

// Lado mayor maximo del video exportado. El VP9 alpha single-thread de wasm
// aborta (OOM) por encima de ~1.5K px de lado, asi que capamos el video aqui
// (la foto PNG no se capa). Ajustable si se necesita mas o menos resolucion.
const VIDEO_MAX_DIM = 1280;

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);

  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Rasteriza el marco con el media oculto (hueco transparente) y mide el rect
 * del viewport y su border-radius, todo a escala natural (sin el transform del
 * preview).
 */
export async function captureOverlay(
  exportNode: HTMLElement,
  maxDim = 0,
): Promise<{ overlay: HTMLCanvasElement; rect: Rect; radius: number; scale: number }> {
  const media = exportNode.querySelector('img, video') as MediaEl | null;

  if (!media) throw new Error('No hay media en el marco');

  const viewport = media.parentElement as HTMLElement;
  // El preview escala el marco con un transform en el ancestro .mk-scale. Hay
  // que anularlo para medir y capturar a resolucion natural, no la reducida.
  const scaled = exportNode.closest('.mk-scale') as HTMLElement | null;
  let prevTransform = '';

  if (scaled) prevTransform = scaled.style.transform;

  const prevVis = media.style.visibility;

  await document.fonts.ready;

  if (scaled) scaled.style.transform = 'none';

  media.style.visibility = 'hidden';

  try {
    const frameBox = exportNode.getBoundingClientRect();
    const vpBox = viewport.getBoundingClientRect();
    const rect: Rect = {
      x: vpBox.left - frameBox.left,
      y: vpBox.top - frameBox.top,
      w: vpBox.width,
      h: vpBox.height,
    };
    // Cap opcional del lado mayor: el VP9 alpha single-thread de wasm aborta
    // (OOM) con frames muy grandes, asi que el video baja de DPR 2 a lo que
    // quepa. La foto PNG (un solo frame) se queda a DPR pleno (maxDim = 0).
    let scale = DPR;

    if (maxDim > 0) {
      const longest = Math.max(frameBox.width, frameBox.height);

      scale = Math.min(DPR, maxDim / longest);
    }

    const radius = parseFloat(getComputedStyle(viewport).borderTopLeftRadius) || 0;
    const dataUrl = await toPng(exportNode, {
      pixelRatio: scale,
      width: frameBox.width,
      height: frameBox.height,
    });
    const overlay = document.createElement('canvas');

    // Dimensiones pares: yuva420p (chroma 4:2:0) exige ancho y alto divisibles
    // por 2; un alto impar (p.ej. 1813) hace abortar al encoder VP9.
    overlay.width = 2 * Math.round((frameBox.width * scale) / 2);
    overlay.height = 2 * Math.round((frameBox.height * scale) / 2);

    const img = new Image();

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('overlay load'));
      img.src = dataUrl;
    });
    overlay.getContext('2d')!.drawImage(img, 0, 0, overlay.width, overlay.height);

    return { overlay, rect, radius, scale };
  } finally {
    media.style.visibility = prevVis;

    if (scaled) scaled.style.transform = prevTransform;
  }
}

/** Dibuja un frame: media recortado (cover) bajo el overlay del marco. */
export function composeFrame(
  ctx: CanvasRenderingContext2D,
  media: MediaEl,
  natural: { w: number; h: number },
  overlay: HTMLCanvasElement,
  rect: Rect,
  radius: number,
  pos: { x: number; y: number },
  scale: number,
): void {
  const { canvas } = ctx;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const s = coverSourceRect(natural, { w: rect.w, h: rect.h }, pos);

  ctx.save();
  roundRectPath(ctx, rect.x * scale, rect.y * scale, rect.w * scale, rect.h * scale, radius * scale);
  ctx.clip();
  ctx.drawImage(media, s.sx, s.sy, s.sw, s.sh, rect.x * scale, rect.y * scale, rect.w * scale, rect.h * scale);
  ctx.restore();
  ctx.drawImage(overlay, 0, 0);
}

/** Compone un unico frame y lo devuelve como PNG transparente. */
export async function exportImagePng(exportNode: HTMLElement): Promise<Blob> {
  const media = exportNode.querySelector('img, video') as MediaEl | null;

  if (!media) throw new Error('No hay media en el marco');

  const natural = mediaNaturalSize(media);
  const { overlay, rect, radius, scale } = await captureOverlay(exportNode);
  const pos = parseObjectPosition(media.style.objectPosition);
  const canvas = document.createElement('canvas');

  canvas.width = overlay.width;
  canvas.height = overlay.height;
  const ctx = canvas.getContext('2d')!;

  composeFrame(ctx, media, natural, overlay, rect, radius, pos, scale);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));

  if (!blob) throw new Error('toBlob fallo');

  return blob;
}

/** "50% 20%" -> { x: 50, y: 20 }; defaults 50/50. */
export function parseObjectPosition(v: string): { x: number; y: number } {
  const parts = v.split(/\s+/).map((p) => parseFloat(p));
  let x = 50;
  let y = 50;

  if (Number.isFinite(parts[0])) x = parts[0];

  if (Number.isFinite(parts[1])) y = parts[1];

  return { x, y };
}

let ffmpegPromise: Promise<FFmpeg> | null = null;

async function getFFmpeg(): Promise<FFmpeg> {
  if (ffmpegPromise) return ffmpegPromise;

  ffmpegPromise = (async () => {
    const ff = new FFmpeg();
    const base = '/ffmpeg';

    await ff.load({
      classWorkerURL: ffmpegClassWorkerURL,
      coreURL: await toBlobURL(`${base}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${base}/ffmpeg-core.wasm`, 'application/wasm'),
    });

    return ff;
  })();

  // Si la carga falla (404, red), no cachear el rechazo: permite reintentar.
  ffmpegPromise.catch(() => {
    ffmpegPromise = null;
  });

  return ffmpegPromise;
}

function seek(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };
    const onSeeked = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Fallo al hacer seek del video'));
    };

    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    video.currentTime = t;
  });
}

async function canvasToPng(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));

  if (!blob) throw new Error('toBlob fallo');

  return new Uint8Array(await blob.arrayBuffer());
}

/** Compone el video frame a frame y codifica WebM alpha + MP4. */
export async function exportVideo(
  exportNode: HTMLElement,
  file: File,
  opts: { fps: number; maxSeconds: number; bg: string; onProgress?: (msg: string) => void },
): Promise<{ webm: Blob; mp4: Blob }> {
  const video = exportNode.querySelector('video') as HTMLVideoElement | null;

  if (!video) throw new Error('No hay video en el marco');

  const natural = { w: video.videoWidth, h: video.videoHeight };

  video.pause();

  const { overlay, rect, radius, scale } = await captureOverlay(exportNode, VIDEO_MAX_DIM);
  const pos = parseObjectPosition(video.style.objectPosition);

  const canvas = document.createElement('canvas');

  canvas.width = overlay.width;
  canvas.height = overlay.height;
  const ctx = canvas.getContext('2d')!;

  const times = frameTimes(video.duration, opts.fps, opts.maxSeconds);
  const ff = await getFFmpeg();
  const w = canvas.width;
  const h = canvas.height;
  const fps = String(opts.fps);
  const inputName = `src.${(file.name.split('.').pop() || 'mp4').toLowerCase()}`;
  const written: string[] = [];

  // Nombres del FS virtual a limpiar al final (evita que frames de un export
  // previo mas largo se cuelen en el siguiente).
  async function cleanup() {
    const targets = [...written, inputName, 'out.webm', 'out.mp4'];

    for (const name of targets) {
      await ff.deleteFile(name).catch(() => undefined);
    }
  }

  try {
    for (let i = 0; i < times.length; i++) {
      await seek(video, times[i]);
      composeFrame(ctx, video, natural, overlay, rect, radius, pos, scale);
      const name = `f${String(i).padStart(5, '0')}.png`;

      await ff.writeFile(name, await canvasToPng(canvas));
      written.push(name);
      opts.onProgress?.(`Capturando ${i + 1}/${times.length}`);
    }

    opts.onProgress?.('Codificando WebM…');
    // WebM con alpha vía VP8 (libvpx), NO VP9 (libvpx-vp9): el encoder VP9 de
    // @ffmpeg/core wasm aborta en el primer frame (abort nativo → printErr con
    // mensaje undefined). VP8 alpha funciona y además tiene soporte de
    // transparencia más amplio en navegadores. `-auto-alt-ref 0` es obligatorio
    // para preservar el canal alfa en VP8.
    await ff.exec([
      '-framerate', fps, '-i', 'f%05d.png',
      '-c:v', 'libvpx', '-pix_fmt', 'yuva420p', '-auto-alt-ref', '0',
      '-b:v', '2M', '-crf', '12',
      'out.webm',
    ]);

    opts.onProgress?.('Codificando MP4…');
    await ff.writeFile(inputName, new Uint8Array(await file.arrayBuffer()));
    await ff.exec([
      '-framerate', fps, '-i', 'f%05d.png',
      '-i', inputName,
      '-filter_complex',
      `color=c=${toFFmpegColor(opts.bg)}:s=${w}x${h}[bg];[bg][0:v]overlay=shortest=1,format=yuv420p[v]`,
      '-map', '[v]', '-map', '1:a?',
      '-c:v', 'libx264', '-preset', 'veryfast', '-crf', '23', '-c:a', 'aac', '-shortest',
      'out.mp4',
    ]);

    const webmData = new Uint8Array(await ff.readFile('out.webm') as Uint8Array);
    const mp4Data = new Uint8Array(await ff.readFile('out.mp4') as Uint8Array);
    const webm = new Blob([webmData], { type: 'video/webm' });
    const mp4 = new Blob([mp4Data], { type: 'video/mp4' });

    return { webm, mp4 };
  } finally {
    await cleanup();
    // El preview quedo pausado por video.pause(); reanuda el bucle.
    video.play().catch(() => undefined);
  }
}
